import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Loader2, CheckCircle2, Download, Users, Image as ImageIcon, ArrowRight } from "lucide-react";
import { useTranslation } from "@/i18n";

const MakeDownload = () => {
  const { t } = useTranslation();
  const { orderId } = useParams();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling");
  const [kit, setKit] = useState(null);

  useEffect(() => {
    let tries = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/make/order/${orderId}/status`);
        if (data.status === "paid") {
          const kitRes = await api.get(`/make/order/${orderId}/kit`);
          setKit(kitRes.data.kit || kitRes.data);
          setStatus("ready");
          try { localStorage.removeItem("wasntme.make.draft"); } catch (e) { /* ignore */ }
          return;
        }
      } catch (err) { /* keep polling */ }
      tries += 1;
      if (tries < 25) setTimeout(poll, 1500);
      else setStatus("timeout");
    };
    poll();
  }, [orderId, sessionId]);

  if (status === "polling") {
    return (
      <div className="grain min-h-screen flex items-center justify-center">
        <div className="card-noir p-8 max-w-md text-center">
          <Loader2 size={28} className="mx-auto animate-spin text-[#d4af37] mb-3" />
          <div className="font-display text-xl text-[#f4f0ec] mb-2">{t("make.mintingTitle")}</div>
          <div className="text-xs text-[#a39797]">
            {t("make.mintingBody")}
          </div>
        </div>
      </div>
    );
  }

  if (status === "timeout") {
    return (
      <div className="grain min-h-screen flex items-center justify-center">
        <div className="card-noir p-8 max-w-md text-center">
          <div className="font-display text-xl text-[#f4f0ec] mb-2">{t("make.mintingTimeoutTitle")}</div>
          <div className="text-xs text-[#a39797] mb-3">
            {t("make.mintingTimeoutBody")}
          </div>
          <Link to="/" className="btn-ghost text-sm">{t("common.backToFile")}</Link>
        </div>
      </div>
    );
  }

  const pdfUrl = `${process.env.REACT_APP_BACKEND_URL}/api/make/order/${orderId}/kit.pdf`;

  return (
    <div className="grain min-h-screen" data-testid="make-download-page">
      <div className="max-w-3xl mx-auto px-6 py-10">
        <div className="text-center mb-10">
          <CheckCircle2 size={40} className="mx-auto text-[#4ade80] mb-3" />
          <div className="font-hand text-[#d4af37] rotate-[-1deg] text-2xl mb-1">{t("make.readyLabel")}</div>
          <h1 className="font-display text-4xl text-[#f4f0ec] mb-2" data-testid="make-case-title" translate="no">
            {kit?.case_title || "An Untitled Mystery"}
          </h1>
          <div className="text-sm text-[#a39797]">
            {(kit?.role_cards || []).length} {t("make.rolesCount")} · {t("make.generatedJust")}
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4 mb-8">
          <a href={pdfUrl} target="_blank" rel="noreferrer"
            className="card-noir p-6 hover:border-[#d4af37] transition-colors"
            data-testid="download-pdf-btn">
            <Download size={20} className="text-[#d4af37] mb-2" />
            <div className="font-display text-xl text-[#f4f0ec]">{t("make.downloadKitTitle")}</div>
            <div className="text-xs text-[#a39797] mt-1">{t("make.downloadKitBody")}</div>
          </a>
          <div className="card-noir p-6">
            <ImageIcon size={20} className="text-[#d4af37] mb-2" />
            <div className="font-display text-xl text-[#f4f0ec]">{t("make.caseClosedTitle")}</div>
            <div className="text-xs text-[#a39797] mt-1">{t("make.caseClosedBody")}</div>
            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/make/order/${orderId}/case-closed.png`}
              target="_blank" rel="noreferrer" className="text-sm text-[#d4af37] mt-2 inline-block">
              {t("make.openPng")}
            </a>
          </div>
        </div>

        {/* Player links */}
        {kit?.role_cards?.some((rc) => rc.player_token) && (
          <div className="card-noir p-6">
            <div className="flex items-center gap-2 mb-3">
              <Users size={14} className="text-[#d4af37]" />
              <div className="font-display text-xl text-[#f4f0ec]">{t("make.playerLinksTitle")}</div>
            </div>
            <p className="text-xs text-[#a39797] mb-3">
              {t("make.playerLinksIntro")}
            </p>
            <div className="space-y-2">
              {kit.role_cards.filter((rc) => rc.player_token).map((rc, i) => {
                const url = `${window.location.origin}/play/${rc.player_token}`;
                return (
                  <div key={i} className="flex items-center justify-between gap-2 border border-[#2d2424] rounded p-2 flex-wrap"
                    data-testid={`player-link-${i}`}>
                    <div className="text-sm">
                      <span className="text-[#f4f0ec]" translate="no">{rc.player_name || `Player ${i + 1}`}</span>
                      <span className="text-[#a39797] ml-2">· {rc.archetype || rc.role || ""}</span>
                    </div>
                    <button onClick={() => { navigator.clipboard.writeText(url); }}
                      className="btn-ghost text-xs">{t("make.copyLinkBtn")}</button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="mt-8 text-center">
          <Link to="/library" className="btn-primary text-sm">
            {t("make.goToLibrary")} <ArrowRight size={14} />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default MakeDownload;
